import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';

// Palette mirrored from the action detail / card headers.
const INK = '#111418';
const TEXT_SECONDARY = '#5b6470';
const TEXT_MUTED = '#8b95a3';
const BORDER = '#e6e8ec';
const BORDER_HOVER = '#d4d8df';
const SURFACE = '#ffffff';
const SURFACE_SUBTLE = '#f7f8fa';
const DONE_FG = '#047857';
const DONE_BG_TILE = '#a7f3d0';

const COPIED_RESET_MS = 1800;

type Props = {
  // Absolute URL to copy. Defaults to the current page.
  url?: string;
  // Icon-only rendering, for tight headers like the action cards.
  compact?: boolean;
};

// Copies a shareable link to the clipboard. Used by the action detail header
// (labelled) and by each action card in the Evaluation & Actions form (compact).
const ShareButton = ({ url, compact }: Props) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleShare = () => {
    if (!navigator.clipboard?.writeText) {
      return;
    }
    navigator.clipboard
      .writeText(url ?? window.location.href)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
      })
      .catch(() => {
        /* clipboard unavailable — no-op */
      });
  };

  const icon = copied ? (
    <CheckIcon sx={{ fontSize: 16 }} />
  ) : (
    <IosShareRoundedIcon sx={{ fontSize: 16 }} />
  );
  const label = copied ? t('action.detail.shareCopied') : t('action.detail.share');

  if (compact) {
    return (
      <Tooltip title={label}>
        <IconButton
          onClick={handleShare}
          size="small"
          aria-label={t('action.detail.share').toString()}
          sx={{
            color: copied ? DONE_FG : TEXT_MUTED,
            '&:hover': { color: copied ? DONE_FG : INK, bgcolor: SURFACE_SUBTLE },
          }}
        >
          {icon}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      onClick={handleShare}
      disableElevation
      startIcon={icon}
      sx={{
        bgcolor: SURFACE,
        color: copied ? DONE_FG : TEXT_SECONDARY,
        fontSize: 13,
        fontWeight: 500,
        px: 1.5,
        py: 0.7,
        borderRadius: '6px',
        border: `1px solid ${copied ? DONE_BG_TILE : BORDER}`,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&:hover': {
          bgcolor: SURFACE_SUBTLE,
          color: copied ? DONE_FG : INK,
          borderColor: copied ? DONE_BG_TILE : BORDER_HOVER,
        },
      }}
    >
      {label}
    </Button>
  );
};

ShareButton.defaultProps = {
  url: undefined,
  compact: false,
};

export default ShareButton;
